﻿import { action, computed, makeObservable, observable, reaction } from "mobx";
import { ChartBackground, ChartDetail, ChartHybrid, ChartType, IChartState } from "../models/ChartModels";
import { IColorScale } from "../models/ColorScale";
import { ColorScaleSpaceIds } from "../models/ColorScales";
import { BathymetryStore } from "./BathymetryStore";
import { IStateStore, StateStore } from "./StateStore";

type ChartLayerLowFull = `${'low' | 'full'}`;
type ChartLayerFull4 = `${'4' | ''}`;
type ChartLayerHybrid = `${'hybrid' | 'hybridsatellite' | 'bathy' | ''}`;
type ChartLayerPaper = `${'paper' | ''}`;

export type ChartLayerDetailLevel = `${ChartLayerLowFull}${ChartLayerHybrid}${ChartLayerPaper}${ChartLayerFull4}` | 'satellite' | 'osm' | 'blank';

interface ChartLayerDetailLevelOptions {
    includeSatellite?: boolean
}

export abstract class ChartBaseStore<T extends IChartState> extends StateStore<T> implements IChartState, IStateStore<T> {

    public type: ChartType = ChartType.ECDIS;
    public hybrid: ChartHybrid = ChartHybrid.None;
    public background: ChartBackground = ChartBackground.OpenStreetMap;
    public detail: ChartDetail = ChartDetail.Low;
    public brightness: number = 0;

    public readonly bathymetry: BathymetryStore;

    constructor(getColorScalesFn?: (additionalLookupKeys?: number[]) => Promise<IColorScale[]>) {
        super();

        makeObservable(this, {
            type: observable,
            hybrid: observable,
            background: observable,
            detail: observable,
            brightness: observable,
            getBaseState: computed,
            layerDetailLevel: computed,           
            setLayerDetailLevel: action.bound
        });

        this.bathymetry = new BathymetryStore(getColorScalesFn);

        // adjust bathymetry type to chart type
        reaction(() => this.type, (type) => {
            const spaceId = this.bathymetry.spaceId;
            if (type === ChartType.ECDIS && spaceId === ColorScaleSpaceIds.Paper)
                this.bathymetry.setState({ spaceId: ColorScaleSpaceIds.ECDIS })
            else if (type === ChartType.Paper && spaceId === ColorScaleSpaceIds.ECDIS)
                this.bathymetry.setState({ spaceId: ColorScaleSpaceIds.Paper });
        })

        // adjust chart type to bathymetry type
        reaction(() => this.bathymetry.spaceId, (spaceId) => {
            const type = this.type;
            if (type === ChartType.ECDIS && spaceId === ColorScaleSpaceIds.Paper)
                this.setState({ type: ChartType.Paper } as T);
            else if (type === ChartType.Paper && spaceId === ColorScaleSpaceIds.ECDIS)
                this.setState({ type: ChartType.ECDIS } as T);
        })
    }   

    public get layerDetailLevel() {
        return this.getLayerDetailLevel_({
            includeSatellite: false
        });   
    }   

    public getLayerDetailLevel(options?: ChartLayerDetailLevelOptions) {
        return this.getLayerDetailLevel_(options);
    }

    public setLayerDetailLevel(type: ChartLayerDetailLevel) {
        const state = this.getState;

        if (type === 'satellite') {
            state.type = ChartType.Satellite;
        }
        else if (type === 'osm') {
            state.type = ChartType.OpenStreetMap;
        }
        else if (type === 'blank') {
            state.type = ChartType.None;
        }
        else {
            if (type.includes('paper')) {
                state.type = ChartType.Paper;
            }
            else {
                state.type = ChartType.ECDIS
            }

            if (type.includes('low')) {
                state.detail = ChartDetail.Low;
            }
            else if (type.includes('full')) {
                if (type.includes('4'))
                    state.detail = ChartDetail.ExtraHigh;
                else
                    state.detail = ChartDetail.High;
            }

            if (type.includes('bathy')) {
                state.hybrid = ChartHybrid.Bathymetry;
            }
            else if (type.includes('hybrid')) {
                state.hybrid = ChartHybrid.Land;
                state.background = type.includes('satellite') ? ChartBackground.Satellite : ChartBackground.OpenStreetMap;
            }
            else { 
                state.hybrid = ChartHybrid.None;
            }
        }

        this.setState(state);
    }

    public get getBaseState(): IChartState {
        const { type, hybrid, background, detail, brightness, bathymetry, layerDetailLevel } = this;
        return {
            type, hybrid, background, detail, brightness, layerDetailLevel,
            bathymetry: bathymetry.getState
        }
    }

    public setState(state: Partial<T>) {
        state = { ...state };

        delete state.layerDetailLevel;

        if (state.bathymetry)
            this.bathymetry.setState({ ...state.bathymetry });
        delete state.bathymetry;

        super.setState(state);
    }

    private getLayerDetailLevel_ = (options?: ChartLayerDetailLevelOptions): ChartLayerDetailLevel => {

        switch (this.type) {
            case ChartType.None:
                return 'blank';
            case ChartType.OpenStreetMap:
                return 'osm';
            case ChartType.Satellite:
                return options?.includeSatellite ? 'satellite' : 'osm';
        }

        const prefix: ChartLayerLowFull = this.detail === ChartDetail.Low ? "low" : "full";
        const suffix: ChartLayerFull4 = this.detail === ChartDetail.ExtraHigh ? "4" : "";
        const paper: ChartLayerPaper = this.type === ChartType.Paper ? 'paper' : '';

        var hybrid: ChartLayerHybrid;
        switch (this.hybrid) {
            case ChartHybrid.Bathymetry:
                hybrid = "bathy"; break;
            case ChartHybrid.Land:
                if (this.background === ChartBackground.Satellite && options?.includeSatellite)
                    hybrid = "hybridsatellite";
                else
                    hybrid = 'hybrid';
                break;
            default:
                hybrid = ''; break
        }

        return `${prefix}${hybrid}${paper}${suffix}`;
    }
}

export class ChartStore extends ChartBaseStore<IChartState> {

    public get getState(): IChartState {
        return this.getBaseState;
    }
}